import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { ConnectionStatus, DisplayOrientation } from "@workspace/mobile";
import { DEFAULT_PALETTE, type Palette } from "../../utils/palette";
import type { RootState } from "../store";

export type Theme = "Light" | "Dark" | "System";

export type DeviceState = {
  theme: Theme;
  palette: Palette;
  lastSyncForDate?: string;
  connectionStatus: ConnectionStatus;
  biometricEnabled: boolean;
  biometricSupported: boolean;
  pinSecure: boolean;
  displayOrientation: DisplayOrientation;
  isAccessButtonEnabled: boolean;
  accessButtonMenuItems: string;
};

export const initialDeviceState: DeviceState = {
  theme: "Light",
  palette: DEFAULT_PALETTE,
  lastSyncForDate: undefined,
  connectionStatus: "internet",
  biometricEnabled: false,
  biometricSupported: false,
  pinSecure: false,
  displayOrientation: "unknown",
  isAccessButtonEnabled: false,
  accessButtonMenuItems: "products"
};

const deviceSlice = createSlice({
  name: "device",
  initialState: initialDeviceState,
  reducers: {
    themeChanged: (state, action: PayloadAction<Theme>) => {
      state.theme = action.payload;
    },
    paletteChosen: (state, action: PayloadAction<Palette>) => {
      state.palette = action.payload;
    },
    paletteLoaded: (state, action: PayloadAction<Palette>) => {
      state.palette = action.payload;
    },
    connectionStatusChanged: (
      state,
      action: PayloadAction<ConnectionStatus>
    ) => {
      state.connectionStatus = action.payload;
    },
    lastSyncRecorded: (state, action: PayloadAction<string>) => {
      state.lastSyncForDate = action.payload;
    },
    biometricEnabledChanged: (state, action: PayloadAction<boolean>) => {
      state.biometricEnabled = action.payload;
    },
    biometricSupportedChanged: (state, action: PayloadAction<boolean>) => {
      state.biometricSupported = action.payload;
    },
    pinSecureChanged: (state, action: PayloadAction<boolean>) => {
      state.pinSecure = action.payload;
    },
    displayOrientationChanged: (
      state,
      action: PayloadAction<DisplayOrientation>
    ) => {
      state.displayOrientation = action.payload;
    },
    accessButtonEnabledChanged: (state, action: PayloadAction<boolean>) => {
      state.isAccessButtonEnabled = action.payload;
    },
    accessButtonMenuItemsChanged: (state, action: PayloadAction<string>) => {
      state.accessButtonMenuItems = action.payload;
    }
  }
});

export const {
  themeChanged,
  paletteChosen,
  paletteLoaded,
  connectionStatusChanged,
  lastSyncRecorded,
  biometricEnabledChanged,
  biometricSupportedChanged,
  pinSecureChanged,
  displayOrientationChanged,
  accessButtonEnabledChanged,
  accessButtonMenuItemsChanged
} = deviceSlice.actions;

export const selectTheme = (state: RootState): Theme => state.device.theme;
export const selectPalette = (state: RootState): Palette =>
  state.device.palette;
export const selectLastSyncForDate = (state: RootState): string | undefined =>
  state.device.lastSyncForDate;
export const selectConnectionStatus = (state: RootState): ConnectionStatus =>
  state.device.connectionStatus;
export const selectBiometricEnabled = (state: RootState): boolean =>
  state.device.biometricEnabled;
export const selectBiometricSupported = (state: RootState): boolean =>
  state.device.biometricSupported;
export const selectPinSecure = (state: RootState): boolean =>
  state.device.pinSecure;
export const selectDisplayOrientation = (
  state: RootState
): DisplayOrientation => state.device.displayOrientation;
export const selectIsAccessButtonEnabled = (state: RootState): boolean =>
  state.device.isAccessButtonEnabled;
export const selectAccessButtonMenuItems = (state: RootState): string =>
  state.device.accessButtonMenuItems;
export const selectColourScheme = (state: RootState): "light" | "dark" =>
  state.device.theme === "Dark" ? "dark" : "light";

export const deviceReducer = deviceSlice.reducer;
export default deviceReducer;
